// The concentric multi-band agent dot. The option (a set of concentric shapes in a
// `0 0 64 64` viewBox, outer band edge-to-edge) is chosen from the shared 20-option
// system in common-lib (`agentDotShapes` / `optionForAgent`), keyed by the same
// avalanche hash on web and native — so an agent's dot reads identically everywhere.
//
// The old local 5-triplet `AGENT_TRIPLETS` / `tripletFor` was retired: geometry and
// color now live entirely in common-lib's `AGENT_OPTIONS`, matching iOS/Android 1:1.
// The shapes render as real SVG <ellipse> elements (a circle is rx === ry) — no
// raw-HTML injection.

import { agentDotShapes } from '@wix/ai-scheduling-assistant-common-lib/entities';

export function AgentDot({
  id,
  size = 20,
  ringColor,
  initial,
}: {
  id: string;
  size?: number;
  ringColor?: string;
  initial?: string;
}) {
  const shapes = agentDotShapes(id);
  return (
    <span
      style={{
        display: 'block',
        position: 'relative',
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: '50%',
        boxShadow: ringColor ? `0 0 0 ${Math.max(1.5, size * 0.075)}px ${ringColor}` : undefined,
      }}
    >
      <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden style={{ display: 'block' }}>
        {shapes.map((s, i) => (
          <ellipse key={i} cx={s.cx} cy={s.cy} rx={s.rx} ry={s.ry} fill={s.fill} />
        ))}
      </svg>
      {/* Centred on the artwork, whose innermost band is light on every one of the 20 options — which is
          why a FIXED dark glyph stays legible in both themes and no per-option ink is needed. Drawing the
          letter on the flat outer accent instead is what makes it unreadable: the deep options
          (`#81252C`) leave about 1.9:1 against it. Same metrics as iOS and Android's `AgentDot`. */}
      {initial ? (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--ds-neutral-950)',
            fontSize: size * 0.42,
            fontWeight: 800,
            lineHeight: 1,
            textTransform: 'uppercase',
          }}
        >
          {initial}
        </span>
      ) : null}
    </span>
  );
}
